import React, {useMemo, useState} from "react";
import {Alert, Card, Tabs, Tag} from 'antd';
import {DollarOutlined, FileSyncOutlined, FileTextOutlined} from '@ant-design/icons';
import PeriodicIncome from "./income/PeriodicIncome";
import OneIncome from "./income/OneIncome";

const {TabPane} = Tabs;

/**
 * Компонент управления доходами
 * Показывает периодические и единоразовые доходы с аналитикой
 **/
const IncomeManagement = (props) => {
    const [activeTab, setActiveTab] = useState('1'); // Активная вкладка

    // Конфигурация типов доходов
    const INCOME_TYPES = {
        PERIODIC: '1',   // Периодические доходы
        ONE_TIME: '2'    // Единоразовые доходы
    };

    /**
     * Расчет статистики по доходам
     */
    const incomeStatistics = useMemo(() => {
        // Расчет периодических доходов
        const calculatePeriodicIncome = () => {
            let total = 0;
            let activeCount = 0;
            let totalBalance = 0;

            props.income?.forEach(item => {
                if (item.enable === 1) {
                    activeCount++;
                    // Суммируем все месячные платежи
                    const monthlyPayments = item.cost.split('`').map(cost =>
                        parseFloat(cost.replace(',', '.')) || 0
                    );
                    total += monthlyPayments.reduce((sum, payment) => sum + payment, 0);
                }

                // Находим баланс для этого объекта
                const balanceItem = props.payments?.find(payment =>
                    payment.object_id === item.obj_id
                );
                if (balanceItem) {
                    totalBalance += parseFloat(balanceItem.cost) || 0;
                }
            });

            return {total, activeCount, totalBalance};
        };

        // Расчет единоразовых доходов
        const calculateOneTimeIncome = () => {
            const oneTimeTotal = props.one_income?.reduce((sum, item) =>
                sum + (parseFloat(item.cost) || 0), 0
            ) || 0;

            const oneTimeCount = props.one_income?.length || 0;

            return {total: oneTimeTotal, count: oneTimeCount};
        };

        const periodic = calculatePeriodicIncome();
        const oneTime = calculateOneTimeIncome();
        const grandTotal = periodic.total + oneTime.total;

        return {
            periodic: {
                total: periodic.total,
                activeCount: periodic.activeCount,
                totalBalance: periodic.totalBalance,
                average: periodic.activeCount > 0 ? periodic.total / periodic.activeCount : 0
            },
            oneTime: {
                total: oneTime.total,
                count: oneTime.count,
                average: oneTime.count > 0 ? oneTime.total / oneTime.count : 0
            },
            grandTotal,
            overallCount: periodic.activeCount + oneTime.count
        };
    }, [props.income, props.one_income, props.payments]);

    /**
     * Вкладки для управления доходами
     */
    const incomeTabs = [
        {
            key: INCOME_TYPES.PERIODIC,
            label: (
                <span>
                    <FileSyncOutlined/>
                    Периодические доходы
                    <Tag color="blue" style={{marginLeft: 8}}>
                        {incomeStatistics.periodic.activeCount} актив.
                    </Tag>
                </span>
            ),
            content: (
                <PeriodicIncome
                    postIncome={props.postIncome}
                    objects={props.objects}
                    income={props.income}
                    payments={props.payments}
                    postPayment={props.postPayment}
                    delPayment={props.delPayment}
                    statistics={incomeStatistics.periodic}
                />
            )
        },
        {
            key: INCOME_TYPES.ONE_TIME,
            label: (
                <span>
                    <FileTextOutlined/>
                    Единоразовые доходы
                    <Tag color="orange" style={{marginLeft: 8}}>
                        {incomeStatistics.oneTime.count} запис.
                    </Tag>
                </span>
            ),
            content: (
                <OneIncome
                    one_income={props.one_income}
                    postOneIncome={props.postOneIncome}
                    statistics={incomeStatistics.oneTime}
                />
            )
        }
    ];

    /**
     * Обработчик изменения вкладки
     */
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    /**
     * Получение активного контента вкладки
     */


    return (
        <div className="income-management">
            {/* Заголовок с общей статистикой */}
            <Card
                title={
                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <DollarOutlined style={{color: '#52c41a', fontSize: '20px'}}/>
                        <span>Управление доходами</span>
                    </div>
                }
                className="income-header-card"
                extra={
                    <Tag color="green" style={{fontSize: '14px', padding: '4px 8px'}}>
                        Общий доход: {incomeStatistics.grandTotal.toFixed(2)}€
                    </Tag>
                }
            >

                {/* Вкладки управления доходами */}
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={incomeTabs.map(tab => ({
                        key: tab.key,
                        label: tab.label,
                        children: tab.content
                    }))}
                    className="income-management-tabs"
                    type="card"
                />
            </Card>
        </div>
    );
};

// Fallback компонент на случай ошибок загрузки
const IncomeManagementWithErrorBoundary = (props) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
        return (
            <Alert
                message="Ошибка загрузки компонента доходов"
                description="Пожалуйста, обновите страницу или проверьте подключение"
                type="error"
                showIcon
            />
        );
    }

    try {
        return <IncomeManagement {...props} />;
    } catch (error) {
        console.error('Ошибка в компоненте IncomeManagement:', error);
        setHasError(true);
        return (
            <Alert
                message="Ошибка отображения данных"
                description="Попробуйте перезагрузить страницу"
                type="warning"
                showIcon
            />
        );
    }
};

export default IncomeManagementWithErrorBoundary;